import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Users, Calendar, AlertOctagon, RefreshCw, Search, ArrowUpDown, TrendingUp, BellRing } from "lucide-react";
import AvatarUploader, { AvatarCircle } from "../../components/AvatarUploader";

const STATUS_META = {
  green:  { color: "#4ade80", label: "OK",       bg: "rgba(74,222,128,0.14)" },
  yellow: { color: "#facc15", label: "Atención", bg: "rgba(250,204,21,0.14)" },
  red:    { color: "#f87171", label: "Urgente",  bg: "rgba(239,68,68,0.14)"  },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.green;
  return <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: m.color, boxShadow: `0 0 8px ${m.color}88` }} title={m.label} />;
}

function fmtDays(n) {
  if (n === null || n === undefined) return "—";
  if (n < 0) return `hace ${Math.abs(n)}d`;
  if (n === 0) return "hoy";
  return `en ${n}d`;
}

export default function AdminClientManagement() {
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState("last_training");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState(null);
  const [showModal, setShowModal] = useState(null); // client_id
  const [detail, setDetail] = useState(null);
  const [plans, setPlans] = useState({ plans: [], durations_months: [] });
  const [form, setForm] = useState({ plan_key: "essential", plan_price_eur: "", payment_date: new Date().toISOString().slice(0,10), access_start_date: "", duration_months: 1, update_frequency_days: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [d, a, p] = await Promise.all([
        api.get(`/admin/client-management?sort_by=${sortBy}`),
        api.get("/admin/client-management/alerts/live"),
        api.get("/admin/client-management/plans"),
      ]);
      setData(d.data); setAlerts(a.data); setPlans(p.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sortBy]);

  const rows = useMemo(() => {
    if (!data) return [];
    const filter = q.trim().toLowerCase();
    if (!filter) return data.rows;
    return data.rows.filter((r) => (r.player_name || "").toLowerCase().includes(filter) || (r.parent_name || "").toLowerCase().includes(filter));
  }, [data, q]);

  const openModal = async (cid) => {
    setShowModal(cid);
    try { const { data: dt } = await api.get(`/admin/client-management/${cid}`); setDetail(dt); } catch (e) { toast.error(formatErr(e)); }
  };

  const submitPayment = async () => {
    if (!form.plan_price_eur || !form.payment_date) return toast.error("Precio y fecha de pago obligatorios");
    if (!form.access_start_date) return toast.error("La fecha de inicio de acceso es obligatoria (base del cálculo del vencimiento)");
    try {
      const payload = {
        client_id: showModal,
        plan_key: form.plan_key,
        plan_price_eur: parseFloat(form.plan_price_eur),
        payment_date: form.payment_date,
        access_start_date: form.access_start_date,
        duration_months: parseInt(form.duration_months),
      };
      if (form.update_frequency_days) payload.update_frequency_days = parseInt(form.update_frequency_days);
      if (form.notes) payload.notes = form.notes;
      await api.post("/admin/client-management/subscriptions", payload);
      toast.success("Pago registrado");
      setForm({ ...form, plan_price_eur: "", access_start_date: "", notes: "" });
      const { data: dt } = await api.get(`/admin/client-management/${showModal}`); setDetail(dt);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteSub = async (sid) => {
    if (!window.confirm("¿Eliminar este pago del historial?")) return;
    try {
      await api.delete(`/admin/client-management/subscriptions/${sid}`);
      const { data: dt } = await api.get(`/admin/client-management/${showModal}`); setDetail(dt);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <AppShell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Gestión de Clientes</h1>
          <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Seguimiento comercial y operativo. Vinculado a jugadores, sesiones y perfiles reales.</p>
        </div>
        <button onClick={load} style={btnGhost}><RefreshCw size={14}/> Actualizar</button>
      </div>

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 24 }}>
          <StatCard icon={Users} color="#e5e5e5" label="Clientes activos" value={data.summary.total_active_clients} testid="cm-total" />
          <StatCard icon={TrendingUp} color="#60a5fa" label="Renuevan este mes" value={data.summary.renewals_this_month} testid="cm-renewals" />
          <StatCard icon={Calendar} color="#facc15" label="Caduca en 7 días" value={data.summary.accesses_expiring_7d} testid="cm-expiring" />
        </div>
      )}

      {alerts && alerts.count > 0 && (
        <div style={{ background: alerts.high_count ? "rgba(239,68,68,0.06)" : "rgba(250,204,21,0.06)",
                      border: `1px solid ${alerts.high_count ? "rgba(239,68,68,0.3)" : "rgba(250,204,21,0.3)"}`,
                      padding: 14, borderRadius: 4, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: alerts.high_count ? "#f87171" : "#facc15", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
            <BellRing size={14}/> {alerts.count} aviso(s) — {alerts.high_count} urgente(s)
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {alerts.alerts.slice(0, 6).map((a, i) => (
              <button key={i} onClick={() => openModal(a.client_id)}
                      style={{ background: "transparent", border: "none", color: "#e5e5e5", textAlign: "left", padding: "4px 0", cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span><b>{a.player_name}</b> · {a.message}</span>
                <span style={{ fontSize: 11, color: a.severity === "high" ? "#f87171" : "#facc15", letterSpacing: "0.08em", textTransform: "uppercase" }}>{a.severity}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, padding: "8px 12px", flex: "1 1 280px" }}>
          <Search size={14} color="#a3a3a3"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar jugador o padre..." style={{ background: "transparent", border: "none", color: "#fff", outline: "none", flex: 1, fontSize: 14 }} data-testid="cm-search" />
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle} data-testid="cm-sort">
          <option value="last_training">Último entrenamiento (más antiguos primero)</option>
          <option value="renewal">Fecha de renovación</option>
          <option value="urgency">Urgencia</option>
          <option value="access">Días hasta caducar</option>
          <option value="name">Nombre</option>
        </select>
      </div>

      <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "24px 40px 2fr 1.3fr 1fr 1fr 60px", padding: "10px 14px", background: "rgba(255,255,255,0.03)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3" }}>
          <span></span><span></span><span>Jugador · Padre</span><span>Plan</span><span>Vence</span><span>Últ. entreno</span><span>Tests</span>
        </div>
        {loading && <div style={{ padding: 20, textAlign: "center", color: "#a3a3a3" }}>Cargando…</div>}
        {!loading && rows.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#a3a3a3" }}>Sin resultados.</div>}
        {rows.map((r) => {
          const m = STATUS_META[r.status] || STATUS_META.green;
          return (
            <button key={r.client_id} onClick={() => openModal(r.client_id)}
                    style={{ display: "grid", gridTemplateColumns: "24px 40px 2fr 1.3fr 1fr 1fr 60px", padding: "12px 14px", borderTop: "1px solid rgba(255,255,255,0.04)", background: "transparent", border: "none", width: "100%", textAlign: "left", cursor: "pointer", alignItems: "center", gap: 4 }}
                    className="qv-cm-row"
                    data-testid={`cm-row-${r.client_id}`}>
              <StatusPill status={r.status}/>
              <AvatarCircle url={r.avatar_url} name={r.player_name} size={32} />
              <div style={{ color: "#fff", fontSize: 14, minWidth: 0 }}>
                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.player_name || "—"}
                  {r.renewal_soon_7d && <span style={{ marginLeft: 6, fontSize: 9, background: "rgba(250,204,21,0.14)", color: "#facc15", padding: "1px 6px", borderRadius: 999, letterSpacing: "0.08em", textTransform: "uppercase" }}>Renueva</span>}
                </div>
                {r.parent_name && <div style={{ fontSize: 11, color: "#a3a3a3", marginTop: 2 }}>{r.parent_name}</div>}
                <div style={{ fontSize: 9, color: "#525252", marginTop: 2, fontFamily: "monospace" }}>ID {r.client_id.slice(0, 8)}</div>
              </div>
              <div style={{ fontSize: 13, color: "#e5e5e5" }}>
                {r.current_plan_key ? <>
                  <b style={{ textTransform: "capitalize" }}>{r.current_plan_key.replace("_", " ")}</b>
                  {r.current_plan_price && <span style={{ marginLeft: 6, color: "#a3a3a3", fontSize: 11 }}>{r.current_plan_price}€</span>}
                </> : <span style={{ color: "#737373", fontSize: 12 }}>Sin plan</span>}
              </div>
              <div style={{ fontSize: 12, color: r.days_until_access_end !== null && r.days_until_access_end <= 7 ? m.color : "#e5e5e5" }}>
                {r.access_until || "—"}
                <div style={{ fontSize: 10, color: "#a3a3a3", marginTop: 2 }}>{fmtDays(r.days_until_access_end)}</div>
              </div>
              <div style={{ fontSize: 12, color: "#a3a3a3" }}>{r.last_training_date || <span style={{ color: "#737373" }}>nunca</span>}</div>
              <div style={{ fontSize: 12, color: r.tests_pending_count > 0 ? "#facc15" : "#525252", textAlign: "center" }}>
                {r.tests_pending_count > 0 ? <><b>{r.tests_pending_count}</b><div style={{fontSize:9,letterSpacing:"0.08em"}}>PEND</div></> : "OK"}
              </div>
            </button>
          );
        })}
      </div>

      {showModal && detail && (
        <div style={modalBg} onClick={(e) => e.target === e.currentTarget && setShowModal(null)}>
          <div style={{ ...modalCard, maxWidth: 640 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 14 }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                <AvatarUploader clientId={detail.client_id} currentUrl={detail.avatar_url} playerName={detail.player_name}
                                onUpdated={(url) => { setDetail({ ...detail, avatar_url: url }); load(); }} size={72} />
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.08em", fontSize: 22 }}>{detail.player_name}</h3>
                  <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 3 }}>{detail.parent_name || "—"}</div>
                  <div style={{ fontSize: 10, color: "#525252", marginTop: 2, fontFamily: "monospace" }}>ID: {detail.client_id}</div>
                </div>
              </div>
              <Link to={`/app/admin/clients/${detail.client_id}`} style={{ ...btnGhost, fontSize: 11, textDecoration: "none", flexShrink: 0 }}>Ficha completa</Link>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
              <MiniStat label="Caduca acceso"    value={detail.access_until || "—"}         sub={fmtDays(detail.days_until_access_end)} color={STATUS_META[detail.status].color}/>
              <MiniStat label="Último entreno"   value={detail.last_training_date || "—"}   sub={detail.last_training_date ? "última fecha en calendario" : "sin entrenos"} color="#a3a3a3"/>
            </div>

            <div style={{ background: "#111", border: "1px solid rgba(255,255,255,0.08)", padding: 12, borderRadius: 3, marginBottom: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 6 }}>
                Registrar nuevo pago
                <div style={{ fontSize: 9, textTransform: "none", letterSpacing: 0, color: "#737373", marginTop: 2 }}>El vencimiento se calcula SIEMPRE desde la <b style={{color:"#facc15"}}>Fecha de inicio de acceso</b> + meses del plan. La fecha de pago solo queda en el historial.</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <select value={form.plan_key} onChange={(e) => setForm({ ...form, plan_key: e.target.value })} style={inputStyle} data-testid="cm-form-plan">
                  {plans.plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                </select>
                <input type="number" step="0.01" value={form.plan_price_eur} onChange={(e) => setForm({ ...form, plan_price_eur: e.target.value })} placeholder="Precio (€)" style={inputStyle} data-testid="cm-form-price" />
                <label style={{ fontSize: 10, color: "#a3a3a3", letterSpacing: "0.08em", textTransform: "uppercase", gridColumn: "1 / -1", marginTop: 2, marginBottom: -4 }}>Fecha de pago</label>
                <input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} placeholder="Fecha pago" style={inputStyle} data-testid="cm-form-date" />
                <label style={{ fontSize: 10, color: "#facc15", letterSpacing: "0.08em", textTransform: "uppercase", gridColumn: "2 / 3", marginTop: 2, marginBottom: -4 }}>Fecha inicio acceso *</label>
                <input type="date" value={form.access_start_date} onChange={(e) => setForm({ ...form, access_start_date: e.target.value })} placeholder="Fecha inicio acceso" style={{ ...inputStyle, borderColor: form.access_start_date ? "rgba(255,255,255,0.1)" : "rgba(250,204,21,0.5)" }} data-testid="cm-form-activation" title="Obligatoria. Base del cálculo del vencimiento (independiente de la fecha de pago)." required />
                <select value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} style={inputStyle}>
                  {plans.durations_months.map((d) => <option key={d} value={d}>{d} {d === 1 ? "mes" : "meses"}</option>)}
                </select>
                <input type="number" value={form.update_frequency_days} onChange={(e) => setForm({ ...form, update_frequency_days: e.target.value })} placeholder="Frecuencia actualización (días)" style={inputStyle} />
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notas" style={{ ...inputStyle, gridColumn: "1 / -1" }} />
              </div>
              <button onClick={submitPayment} style={{ ...btnPrimary, marginTop: 10 }} data-testid="cm-form-save">Registrar pago</button>
            </div>

            <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 8 }}>Historial ({detail.subscriptions.length})</div>
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {detail.subscriptions.length === 0 && <div style={{ fontSize: 13, color: "#737373", padding: 10, textAlign: "center" }}>Sin pagos registrados aún.</div>}
              {detail.subscriptions.map((s) => (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed rgba(255,255,255,0.06)", fontSize: 13 }}>
                  <div>
                    <b style={{ textTransform: "capitalize" }}>{s.plan_key.replace("_", " ")}</b>
                    <span style={{ color: "#a3a3a3", marginLeft: 8 }}>Pago {s.payment_date}</span>
                    {s.access_start_date && (
                      <span style={{ color: "#facc15", marginLeft: 8, fontSize: 11 }}>Acceso desde {s.access_start_date}</span>
                    )}
                    <span style={{ color: "#a3a3a3", marginLeft: 8 }}>· {s.duration_months} {s.duration_months === 1 ? "mes" : "meses"}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ color: "#4ade80", fontWeight: 600 }}>{s.plan_price_eur}€</span>
                    <button onClick={() => deleteSub(s.id)} style={{ background: "transparent", border: "none", color: "#f87171", fontSize: 11, cursor: "pointer" }}>×</button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowModal(null); setDetail(null); }} style={btnGhost}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function StatCard({ icon: Ic, color, label, value, testid }) {
  return (
    <div className="qv-card-anim" style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 14, borderRadius: 4 }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, color, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}><Ic size={13}/> {label}</div>
      <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 36, color, lineHeight: 1, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, sub, color }) {
  return (
    <div style={{ background: "#111", border: "1px solid rgba(255,255,255,0.08)", padding: 10, borderRadius: 3 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3" }}>{label}</div>
      <div style={{ fontSize: 15, color: "#fff", marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

const inputStyle = { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 10px", fontSize: 13, borderRadius: 3, outline: "none", boxSizing: "border-box", width: "100%" };
const selectStyle = { ...inputStyle, minWidth: 200 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 5, background: "#dc2626", color: "#fff", border: "none", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 2 };
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#e5e5e5", padding: "7px 12px", fontSize: 12, cursor: "pointer", borderRadius: 2 };
const modalBg = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 };
const modalCard = { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.14)", padding: 20, borderRadius: 4, width: "100%", maxHeight: "90vh", overflowY: "auto" };
